import React, { useState, useEffect } from 'react';
import { 
  Clock, CheckCircle, XCircle, AlertCircle, Send, Link2, Copy, Check,
  Search, Eye, Trash2, User, Mail, Phone,
  Package, X
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const ManageBargains = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [bargainRequests, setBargainRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showGenerateLinkModal, setShowGenerateLinkModal] = useState(false);
  const [linkGenerated, setLinkGenerated] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Form states
  const [finalPrice, setFinalPrice] = useState('');
  const [expiryHours, setExpiryHours] = useState(72);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Status options
  const statusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    { value: 'no_response', label: 'No Response', color: 'bg-gray-100 text-gray-800', icon: XCircle },
    { value: 'contacted', label: 'Contacted Customer', color: 'bg-blue-100 text-blue-800', icon: Phone },
    { value: 'link_sent', label: 'Link Sent', color: 'bg-purple-100 text-purple-800', icon: Send },
    { value: 'transaction_success', label: 'Transaction Success', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
    { value: 'expired', label: 'Expired', color: 'bg-gray-100 text-gray-600', icon: Clock },
  ];

  const getStatusConfig = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  // Fetch bargain requests
  const fetchBargainRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/bargain-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBargainRequests(response.data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching bargain requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBargainRequests();
  }, [token]);

  // Filter requests based on tab
  const pendingStatuses = ['pending'];
  const actionTakenStatuses = ['no_response', 'contacted', 'link_sent', 'transaction_success', 'rejected', 'expired'];

  const filteredRequests = bargainRequests.filter(req => {
    const matchesTab = activeTab === 'pending' 
      ? pendingStatuses.includes(req.status)
      : actionTakenStatuses.includes(req.status);
    
    const matchesSearch = searchTerm === '' || 
      req.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesTab && matchesSearch;
  });

  // Update status
  const updateStatus = async (requestId, newStatus, notes = '') => {
    setUpdating(true);
    try {
      await axios.put(`${API_BASE_URL}/api/bargain-requests/${requestId}`, {
        status: newStatus,
        admin_notes: notes || adminNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchBargainRequests();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // Generate purchase link
  const generatePurchaseLink = async () => {
    if (!selectedRequest) return;
    
    setUpdating(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/bargain-requests/${selectedRequest.id}/generate-link`,
        {
          final_price: parseFloat(finalPrice) || selectedRequest.customer_quote,
          expiry_hours: parseInt(expiryHours) || 72
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setLinkGenerated(response.data.purchase_link);
        await fetchBargainRequests();
      }
    } catch (error) {
      console.error('Error generating link:', error);
      alert('Failed to generate purchase link');
    } finally {
      setUpdating(false);
    }
  };

  // Copy link to clipboard
  const copyLink = () => {
    if (linkGenerated) {
      navigator.clipboard.writeText(linkGenerated);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // Delete request
  const deleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this bargain request?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/api/bargain-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchBargainRequests();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Failed to delete request');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setFinalPrice(request.final_price?.toString() || request.customer_quote?.toString() || '');
    setAdminNotes(request.admin_notes || '');
    setShowDetailModal(true);
  };

  const openGenerateLinkModal = (request) => {
    setSelectedRequest(request);
    setFinalPrice(request.customer_quote?.toString() || '');
    setExpiryHours(72);
    setLinkGenerated(null);
    setLinkCopied(false);
    setShowGenerateLinkModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900">Manage Bargains</h1>
          </div>

          {/* Tabs with Search */}
          <div className="border-b border-gray-200">
            <div className="flex items-center justify-between">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`py-3 px-6 text-sm font-medium border-b-2 flex items-center gap-2 ${
                    activeTab === 'pending'
                      ? 'border-theme-blue text-theme-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  Pending Requests
                </button>
                <button
                  onClick={() => setActiveTab('actioned')}
                  className={`py-3 px-6 text-sm font-medium border-b-2 flex items-center gap-2 ${
                    activeTab === 'actioned'
                      ? 'border-theme-blue text-theme-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <CheckCircle className="h-4 w-4" />
                  Action Taken
                </button>
              </nav>
              
              {/* Search in Tab Header */}
              <div className="relative pr-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-48"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading requests...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No requests found</h3>
              <p className="text-gray-500 mt-1">
                {activeTab === 'pending' 
                  ? 'No pending bargain requests at the moment'
                  : 'No actioned requests to display'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Offer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequests.map((request) => {
                    const statusConfig = getStatusConfig(request.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {request.product_image_url ? (
                              <img 
                                src={request.product_image_url} 
                                alt={request.product_name}
                                className="h-10 w-10 rounded-lg object-contain bg-gray-100"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Package className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{request.product_name}</p>
                              <p className="text-xs text-gray-500">Qty: {request.quantity}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-gray-900">
                            {request.first_name} {request.last_name}
                          </p>
                          <p className="text-xs text-gray-500">{request.email}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-semibold text-orange-600">
                              ${request.customer_quote?.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">
                              Listed: ${request.original_price?.toFixed(2)}
                            </p>
                            {request.final_price && (
                              <p className="text-xs text-green-600 font-medium">
                                Final: ${request.final_price?.toFixed(2)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(request.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openDetailModal(request)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {request.status === 'pending' && (
                              <button
                                onClick={() => openGenerateLinkModal(request)}
                                className="p-1.5 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded"
                                title="Generate Purchase Link"
                              >
                                <Link2 className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteRequest(request.id)}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-xl" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-semibold text-white">Bargain Request Details</h3>
              <button onClick={() => setShowDetailModal(false)} className="text-white hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Product Info */}
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                {selectedRequest.product_image_url ? (
                  <img 
                    src={selectedRequest.product_image_url} 
                    alt={selectedRequest.product_name}
                    className="w-16 h-16 object-contain rounded-lg bg-white"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Package className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <h4 className="font-semibold text-gray-900">{selectedRequest.product_name}</h4>
                  <p className="text-sm text-gray-600">Quantity: {selectedRequest.quantity}</p>
                </div>
              </div>

              {/* Price Info */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Original Price</p>
                  <p className="text-lg font-semibold text-gray-900">${selectedRequest.original_price?.toFixed(2)}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-orange-600">Customer Offer</p>
                  <p className="text-lg font-semibold text-orange-600">${selectedRequest.customer_quote?.toFixed(2)}</p>
                </div>
                {selectedRequest.final_price && (
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-green-600">Final Price</p>
                    <p className="text-lg font-semibold text-green-600">${selectedRequest.final_price?.toFixed(2)}</p>
                  </div>
                )}
              </div>

              {/* Customer Info */}
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-700">Customer Information</h5>
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{selectedRequest.first_name} {selectedRequest.last_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{selectedRequest.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{selectedRequest.phone}</span>
                  </div>
                  {selectedRequest.address && (
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400 text-sm">📍</span>
                      <span className="text-sm">{selectedRequest.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Notes */}
              {selectedRequest.notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h5 className="text-sm font-medium text-blue-800 mb-1">Customer Notes:</h5>
                  <p className="text-sm text-blue-700">{selectedRequest.notes}</p>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder="Add notes about this request..."
                />
              </div>

              {/* Status Update */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Update Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {statusOptions.filter(s => s.value !== 'pending').map((status) => {
                    const StatusIcon = status.icon;
                    return (
                      <button
                        key={status.value}
                        onClick={() => updateStatus(selectedRequest.id, status.value)}
                        disabled={updating || selectedRequest.status === status.value}
                        className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                          selectedRequest.status === status.value
                            ? status.color + ' cursor-default'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            {selectedRequest.status !== 'transaction_success' && selectedRequest.status !== 'expired' && (
              <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    openGenerateLinkModal(selectedRequest);
                  }}
                  className="w-full py-2 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                >
                  <Link2 className="h-4 w-4" />
                  Generate Purchase Link
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Generate Link Modal */}
      {showGenerateLinkModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 rounded-t-xl" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Generate Purchase Link
                </h3>
                <button 
                  onClick={() => setShowGenerateLinkModal(false)} 
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {linkGenerated ? (
                // Success state
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Link Generated!</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Share this link with the customer to complete their purchase at the agreed price.
                  </p>
                  
                  <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <p className="text-xs text-gray-500 mb-1">Purchase Link:</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={linkGenerated}
                        readOnly
                        className="flex-1 px-2 py-1 text-xs bg-white border rounded truncate"
                      />
                      <button
                        onClick={copyLink}
                        className={`px-3 py-1 rounded text-sm font-medium flex items-center gap-1 ${
                          linkCopied 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                      >
                        {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        {linkCopied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mb-4">
                    💡 Tip: Email integration coming soon! For now, please copy the link and send it manually.
                  </p>
                  
                  <button
                    onClick={() => setShowGenerateLinkModal(false)}
                    className="px-6 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                // Form state
                <>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">{selectedRequest.product_name}</p>
                    <p className="text-xs text-gray-500">
                      Customer Offer: ${selectedRequest.customer_quote?.toFixed(2)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Final Price *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={finalPrice}
                        onChange={(e) => setFinalPrice(e.target.value)}
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      This is the price the customer will pay
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link Expiry
                    </label>
                    <select
                      value={expiryHours}
                      onChange={(e) => setExpiryHours(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value={24}>24 hours</option>
                      <option value={48}>48 hours</option>
                      <option value={72}>72 hours (3 days)</option>
                      <option value={168}>7 days</option>
                      <option value={336}>14 days</option>
                    </select>
                  </div>

                  <button
                    onClick={generatePurchaseLink}
                    disabled={updating || !finalPrice}
                    className="w-full py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    {updating ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-5 w-5" />
                        Generate Link
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBargains;
